import { FC, useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import useCheckoutDesign from 'api/useCheckoutDesign';
import { TRIMMED_INPUT_REGEX } from 'constants/constants';
import { EActiveState } from 'constants/enums';
import { useFormik } from 'formik';
import { motion } from 'framer-motion';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { EToasterStatus, showToast } from 'utils/showToast';
import * as yup from 'yup';

import FormTabs from 'components/FormTabs/FormTabs';

import FormHeader from '../../components/FormHeader/FormHeader';

import PreviewSection from './Sections/PreviewSection';
import CheckoutDesignTab from './Tabs/CheckoutDesignTab';
import CheckoutDisplayTab from './Tabs/CheckoutDisplayTab';
import SettingsTab from './Tabs/SettingsTab';
import {
  CheckoutDesign,
  CheckoutDesignFormProps,
  DEFAULT_LOGO,
  ECheckoutDesignTabs,
  STATUS_OPTIONS,
  tabTitles
} from './types';

const CheckoutDesignForm: FC<CheckoutDesignFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { templateId, publisherId } = useParams();
  const navigate = useNavigate();
  const {
    getCheckoutDesigns,
    getCheckoutDesign,
    addCheckoutDesign,
    updateCheckoutDesign
  } = useCheckoutDesign(publisherId, templateId);

  const [activeTab, setActiveTab] = useState<ECheckoutDesignTabs>(
    ECheckoutDesignTabs.SETTINGS
  );
  const [data, setData] = useState<Partial<CheckoutDesign>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isActive, setActive] = useState<EActiveState>(
    data?.isActive ? EActiveState.ACTIVE : EActiveState.INACTIVE
  );
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (templateId && publisherId) {
      getCheckoutDesign.refetch();
    }
  }, [templateId, publisherId]);

  useEffect(() => {
    if (data) setIsLoading(false);
  }, [edit, data]);

  useEffect(() => {
    if (templateId && !getCheckoutDesign.isLoading && getCheckoutDesign.data) {
      setData(getCheckoutDesign.data);
    }
  }, [getCheckoutDesign.data, getCheckoutDesign.isLoading, templateId]);

  const checkUniqueness = useCallback(
    (value: string | undefined, field: keyof CheckoutDesign) => {
      const isTaken = getCheckoutDesigns.data?.find((o: CheckoutDesign) => {
        if (!edit) return o[field] === value;
        return o[field] === value && data[field] !== value;
      });
      return !isTaken;
    },
    [edit, data, getCheckoutDesigns.data]
  );

  const checkoutSchema = yup.object().shape({
    name: yup
      .string()
      .matches(TRIMMED_INPUT_REGEX, 'Name should not be spaces')
      .min(3, `Name length should be longer`)
      .required('Name is required')
      .test('uniqueness', 'Name already exists', (value) => {
        return checkUniqueness(value, 'name');
      }),
    externalTemplateId: yup
      .string()
      .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
      .required('External ID is required')
      .test('uniqueness', 'External ID already exists', (value) => {
        return checkUniqueness(value, 'externalTemplateId');
      })
  });

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  } = useFormik({
    validateOnMount: true,
    validationSchema: checkoutSchema,
    enableReinitialize: true,
    initialValues: {
      externalTemplateId: data?.externalTemplateId || '',
      templateId: templateId || '',
      name: data?.name ? `${data.name}${dup ? '_copy' : ''}` : '',
      isActive: data?.isActive ?? true,
      logo: data?.logo || DEFAULT_LOGO,
      buttonColor: data?.buttonColor || '#448AE3',
      buttonTextColor: data?.buttonTextColor || '#FEF2F2',
      primaryColor: data?.primaryColor || '#FAFAFA',
      textColor: data?.textColor || '#000000',
      backgroundImage: data?.backgroundImage,
      mobileBgImage: data?.mobileBgImage,
      filterAPMs: data?.filterAPMs ?? true,
      filterWallets: data?.filterWallets || ['google'],
      expandProductList: data?.expandProductList ?? false
    },
    onSubmit: async (values) => {
      const newCheckoutDesign: CheckoutDesign = {
        ...values,
        templateId: templateId
      };

      const isUpdate = edit && templateId && !dup;
      const action = isUpdate ? updateCheckoutDesign : addCheckoutDesign;
      const args: any = isUpdate
        ? { form: newCheckoutDesign, templateId }
        : newCheckoutDesign;

      await action.mutateAsync(args, {
        onSuccess: () => {
          getCheckoutDesigns.refetch();
          navigate('../');
          showToast({
            message: `Checkout design ${isUpdate ? 'edited' : 'added'} successfully`,
            status: EToasterStatus.SUCCESS
          });
        },
        onError: () => {
          showToast({
            message: `Error ${isUpdate ? 'editing' : 'adding'} checkout design`,
            status: EToasterStatus.ERROR
          });
        }
      });
    }
  });

  useEffect(() => {
    setActive(values.isActive ? EActiveState.ACTIVE : EActiveState.INACTIVE);
  }, [values?.isActive]);

  const updateCheckoutStatus = async (status: string) => {
    const isActive = status === EActiveState.ACTIVE;

    if (edit) {
      try {
        await updateCheckoutDesign.mutateAsync({
          form: { isActive },
          templateId
        });
        setFieldValue('isActive', isActive);
      } catch (error) {
        showToast({
          message: 'Error editing checkout design',
          status: EToasterStatus.ERROR
        });
      }
    } else {
      setFieldValue('isActive', isActive);
    }
    setActive(status as EActiveState);
  };

  useUnsavedChanges({ dirty });

  const formikProps = {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-hidden"
    >
      <FormHeader
        onCloseForm={() => navigate('../')}
        onMainButtonClick={() => submitForm()}
        onOptionChange={updateCheckoutStatus}
        selectedOption={isActive}
        options={STATUS_OPTIONS}
        title={`${edit ? 'Edit' : 'Create'} Checkout design`}
        isFormValid={!isLoading && isValid && (!edit || dirty)}
        mainButtonText="Save"
        secondaryButtonText={showPreview ? 'Hide Preview' : 'Preview'}
        onSecondaryButtonClick={() => setShowPreview(!showPreview)}
      />
      <div className="flex">
        <div className={showPreview ? 'w-1/2' : 'w-full'}>
          <div className="w-[456px] mx-auto">
            <FormTabs
              tabs={tabTitles}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
          {templateId && getCheckoutDesign.isLoading ? (
            <CircularProgress className="absolute top-1/2 left-1/2 size-16" />
          ) : (
            <div className="mt-7 w-full mx-auto overflow-y-auto h-[calc(100vh-160px)]">
              {activeTab === ECheckoutDesignTabs.SETTINGS && (
                <SettingsTab formikProps={formikProps} />
              )}
              {activeTab === ECheckoutDesignTabs.CHECKOUT_DESIGN && (
                <CheckoutDesignTab formikProps={formikProps} />
              )}
              {activeTab === ECheckoutDesignTabs.CHECKOUT_DISPLAY && (
                <CheckoutDisplayTab formikProps={formikProps} />
              )}
            </div>
          )}
        </div>

        {showPreview && <PreviewSection {...values} />}
      </div>
    </motion.div>
  );
};

export default CheckoutDesignForm;
